import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Tabs } from 'antd'
import { useSelector } from 'react-redux'

import { PolicyRule, ResumeAirRequestQuoteAirs } from 'src/types'

import { ApplicationState } from 'src/store'
import { SearchesType } from 'src/core/store/ducks/air/types'

import { PickFareProps } from '.'
import SearchResultItem from '../SearchResultItem'

type Props = PickFareProps & {
  setPolicyListCheck(list: ResumeAirRequestQuoteAirs[]): void
  policyRule: PolicyRule
}

function MultiStretch({
  onPickFare,
  policyRule,
  selectedFares,
  setPolicyListCheck
}: Props): JSX.Element {
  const { air } = useSelector((state: ApplicationState) => ({
    air: state.air
  }))

  const searchKeys = useMemo(() => {
    return Object.keys(air.searches).sort()
  }, [air.searches])

  const [activeKey, setActiveKey] = useState<string>(searchKeys[0])
  const [airList, setAirList] = useState<SearchesType>({})

  useEffect(() => {
    setPolicyListCheck(
      air.searches[activeKey].list[0]
        ? air.searches[activeKey].list[0].request_quote_airs
        : []
    )
  }, [air.searches, setPolicyListCheck, activeKey])

  useEffect(() => {
    const finishLoading = Object.values(air.searches).every(
      ({ loading }) => !loading
    )

    if (finishLoading) {
      const hasCustomerSupplierPolicy =
        policyRule?.policy_request_general_rule?.service_by_customer_supplier

      if (!selectedFares.length || !hasCustomerSupplierPolicy) {
        setAirList(air.searches)
        return
      }

      if (selectedFares.length > 1) {
        return
      }

      const searches = Object.entries(air.searches).reduce(
        (accSearches, [key, value]) => ({
          ...accSearches,
          [key]: {
            ...value,
            list: value.list.map(resume => ({
              ...resume,
              request_quote_airs: resume.request_quote_airs.reduce(
                (acc, cur) => {
                  const hasSupplier = cur.request_quote_air_fares.filter(
                    ({ customer_supplier_id }) =>
                      customer_supplier_id ===
                      selectedFares[0].customer_supplier_id
                  )

                  if (!hasSupplier.length) {
                    return acc
                  }

                  return [
                    ...acc,
                    { ...cur, request_quote_air_fares: hasSupplier }
                  ]
                },
                []
              )
            }))
          }
        }),
        {}
      )

      setAirList(searches)
      return
    }
    setAirList({})
  }, [air.searches, policyRule, selectedFares])

  useEffect(() => {
    const indexCurrentKey = searchKeys.findIndex(i => i === activeKey)

    if (indexCurrentKey < searchKeys.length - 1 && selectedFares.length) {
      setActiveKey(searchKeys[indexCurrentKey + 1])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFares])

  return (
    <Tabs
      data-test-id={`air-result-panel-card`}
      defaultActiveKey={searchKeys[0]}
      onChange={activeKey => {
        setActiveKey(activeKey)

        setPolicyListCheck(
          air.searches[activeKey].list[0]
            ? air.searches[activeKey].list[0].request_quote_airs
            : []
        )
      }}
      activeKey={activeKey}
    >
      {searchKeys.map((searchKey, index) => (
        <Tabs.TabPane
          data-test-id={`air-result-panel-${index}-${searchKey}-list`}
          tab={searchKey}
          key={searchKey}
        >
          <>
            {air.searches[searchKey]?.error && (
              <Alert
                closable={false}
                message={air.searches[searchKey].error}
                type="error"
              />
            )}
            <SearchResultItem
              selectedFares={selectedFares}
              onPickFare={onPickFare}
              searchKey={activeKey}
              list={airList[searchKey]?.list[0]?.request_quote_airs || []}
            />
          </>
        </Tabs.TabPane>
      ))}
    </Tabs>
  )
}

export default MultiStretch
